import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, useTheme, FormControlLabel, Switch, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import { db, doc, setDoc, collection, addDoc, deleteDoc, getDocs, onSnapshot } from '../firebaseConfig';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';

const TradeAccountForm = ({ userID, onClose, tradeAccounts: initialTradeAccounts }) => {
  const [accountName, setAccountName] = useState('');
  const [initialCapital, setInitialCapital] = useState('');
  const [syncEnabled, setSyncEnabled] = useState(false); // Remains false since disabled
  const [brokerAccountNumber, setBrokerAccountNumber] = useState('');
  const [brokerPassword, setBrokerPassword] = useState('');
  const [brokerServer, setBrokerServer] = useState('');
  const [tradeAccounts, setTradeAccounts] = useState(initialTradeAccounts || []);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [confirmDeleteText, setConfirmDeleteText] = useState('');
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    const fetchTradeAccounts = async () => {
      try {
        const tradeAccountsRef = collection(db, 'Users', userID, 'tradeAccounts');
        const snapshot = await getDocs(tradeAccountsRef);
        const accounts = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.id,
          initialCapital: doc.data().initialCapital || 0,
          currentCapital: doc.data().initialCapital || 0,
          ...doc.data(),
        }));
        setTradeAccounts(accounts);

        accounts.forEach(account => {
          const capitalPath = `Users/${userID}/tradeAccounts/${account.id}/capital`;
          onSnapshot(collection(db, capitalPath), (capitalSnapshot) => {
            let totalDeposits = 0;
            let totalWithdrawals = 0;
            capitalSnapshot.forEach(doc => {
              const data = doc.data();
              totalDeposits += data.deposit || 0;
              totalWithdrawals += data.withdrawal || 0;
            });
            const currentCapital = totalDeposits - totalWithdrawals;
            setTradeAccounts(prev =>
              prev.map(acc =>
                acc.id === account.id ? { ...acc, currentCapital } : acc
              )
            );
          });
        });
      } catch (error) {
        console.error('Error fetching trade accounts:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!initialTradeAccounts || initialTradeAccounts.length === 0) {
      fetchTradeAccounts();
    } else {
      setTradeAccounts(initialTradeAccounts.map(acc => ({
        ...acc,
        initialCapital: acc.initialCapital || 0,
        currentCapital: acc.currentCapital || acc.initialCapital || 0,
      })));
      initialTradeAccounts.forEach(account => {
        const capitalPath = `Users/${userID}/tradeAccounts/${account.id}/capital`;
        onSnapshot(collection(db, capitalPath), (capitalSnapshot) => {
          let totalDeposits = 0;
          let totalWithdrawals = 0;
          capitalSnapshot.forEach(doc => {
            const data = doc.data();
            totalDeposits += data.deposit || 0;
            totalWithdrawals += data.withdrawal || 0;
          });
          const currentCapital = totalDeposits - totalWithdrawals;
          setTradeAccounts(prev =>
            prev.map(acc =>
              acc.id === account.id ? { ...acc, currentCapital } : acc
            )
          );
        });
      });
      setLoading(false);
    }
  }, [userID, initialTradeAccounts]);

  const handleSubmit = async () => {
    if (!accountName.trim() || !initialCapital.trim()) return;

    try {
      const newAccountID = `${accountName.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;
      const accountRef = doc(db, 'Users', userID, 'tradeAccounts', newAccountID);
      const capitalRef = collection(db, 'Users', userID, 'tradeAccounts', newAccountID, 'capital');
      const initialCapitalValue = parseFloat(initialCapital);

      await setDoc(accountRef, {
        name: accountName,
        isDefault: false,
        broker: null, // No sync for now
        createdAt: new Date().toISOString(),
        syncEnabled: false, // Fixed to false
        syncCredentials: null, // No credentials saved
        initialCapital: initialCapitalValue,
      });

      await addDoc(capitalRef, {
        deposit: initialCapitalValue,
        date: new Date().toISOString(),
        isInitialDeposit: true,
      });

      setTradeAccounts([...tradeAccounts, { id: newAccountID, name: accountName, initialCapital: initialCapitalValue, currentCapital: initialCapitalValue }]);
      setAccountName('');
      setInitialCapital('');
      console.log(`Created trade account: ${accountName}`);
    } catch (error) {
      console.error('Error creating trade account:', error);
    }
  };

  const handleDelete = async () => {
    if (accountToDelete && confirmDeleteText === accountToDelete.name) {
      try {
        const accountRef = doc(db, 'Users', userID, 'tradeAccounts', accountToDelete.id);
        const capitalCollectionRef = collection(db, 'Users', userID, 'tradeAccounts', accountToDelete.id, 'capital');
  
        // Delete all documents in the capital subcollection
        const capitalSnapshot = await getDocs(capitalCollectionRef);
        const deletePromises = capitalSnapshot.docs.map((capitalDoc) =>
          deleteDoc(doc(db, capitalCollectionRef.path, capitalDoc.id))
        );
        await Promise.all(deletePromises);
  
        // Delete the trade account document
        await deleteDoc(accountRef);
  
        // Update the local state to remove the deleted account
        setTradeAccounts(tradeAccounts.filter((acc) => acc.id !== accountToDelete.id));
        setDeleteDialogOpen(false);
        setAccountToDelete(null);
        setConfirmDeleteText('');
        console.log(`Deleted trade account: ${accountToDelete.name} and its capital subcollection`);
      } catch (error) {
        console.error('Error deleting trade account and its capital subcollection:', error);
      }
    }
  };

  if (loading) return <MDTypography>Loading...</MDTypography>;

  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{
        sx: { bgcolor: theme.palette.background.default, color: theme.palette.text.primary, borderRadius: 'lg', minWidth: '300px', p: 2 },
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center" px={2}>
        <MDTypography variant="h5" gutterBottom>Create A New Trade Account</MDTypography>
      </MDBox>
      <DialogContent>
        <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <TextField
            label="Account Name"
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            fullWidth
            margin="normal"
            error={tradeAccounts.length >= 5}
            helperText={tradeAccounts.length >= 5 ? 'Max 5 accounts reached' : ''}
          />
          <TextField
            label="Initial Capital ($)"
            value={initialCapital}
            onChange={(e) => {
              const value = e.target.value;
              if (/^\d*\.?\d*$/.test(value)) setInitialCapital(value);
            }}
            fullWidth
            margin="normal"
            type="text"
            error={tradeAccounts.length >= 10 || (initialCapital && parseFloat(initialCapital) <= 0)}
            helperText={
              tradeAccounts.length >= 10 ? 'Max 10 accounts reached' :
              initialCapital && parseFloat(initialCapital) <= 0 ? 'Must be greater than 0' : ''
            }
          />
          <FormControlLabel
            control={<Switch checked={syncEnabled} disabled />}
            label="Enable Broker Sync (MT4/MT5) - Coming Soon"
          />
          {/* Remove sync fields since toggle is disabled */}
          <MDBox mt={2} width="100%">
            <MDTypography variant="h6">Current Trade Accounts</MDTypography>
            {tradeAccounts.filter(acc => acc.id !== 'default').length === 0 ? (
              <MDTypography variant="body2" color="text">No custom trade accounts yet.</MDTypography>
            ) : (
              tradeAccounts
                .filter(acc => acc.id !== 'default')
                .map((account) => (
                  <Accordion key={account.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <MDTypography>
                        {account.name} (${(account.currentCapital !== undefined ? account.currentCapital : account.initialCapital || 0).toFixed(2)})
                      </MDTypography>
                    </AccordionSummary>
                    <AccordionDetails>
                    <MDButton
                      variant="contained"
                      color="error"
                      onClick={() => {
                        setAccountToDelete(account);
                        setDeleteDialogOpen(true);
                      }}
                      disabled={account.id === 'default'}
                    >
                      Delete
                    </MDButton>
                    </AccordionDetails>
                  </Accordion>
                ))
            )}
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
        <MDBox display="flex" justifyContent="space-between" width="100%">
          <MDButton onClick={onClose} variant="contained" color="warning">Cancel</MDButton>
          <MDButton
            onClick={handleSubmit}
            disabled={!accountName.trim() || tradeAccounts.length >= 5 || !initialCapital || parseFloat(initialCapital) <= 0}
            variant="contained"
            color="warning"
          >
            Create
          </MDButton>
        </MDBox>
      </DialogActions>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: { bgcolor: theme.palette.background.default, color: theme.palette.text.primary, borderRadius: 'lg', minWidth: '300px', p: 2 },
        }}
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="center" px={2}>
          <MDTypography variant="h5" gutterBottom>Confirm Delete</MDTypography>
        </MDBox>
        <DialogContent>
          <MDTypography color="error">
            Warning: Deleting "{accountToDelete?.name}" is permanent and cannot be undone. All associated trades will be lost.
          </MDTypography>
          <TextField
            label={`Type "${accountToDelete?.name}" to confirm`}
            value={confirmDeleteText}
            onChange={(e) => setConfirmDeleteText(e.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <MDBox display="flex" justifyContent="space-between" width="100%">
            <MDButton onClick={() => setDeleteDialogOpen(false)} variant="contained" color="warning">Cancel</MDButton>
            <MDButton
              onClick={handleDelete}
              variant="contained"
              color="error"
              disabled={confirmDeleteText !== accountToDelete?.name}
            >
              Delete
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default TradeAccountForm;